/**
 * Display formatting for the UI. Pure string helpers — no state, no DOM. Internally the app
 * works in metres and milliseconds; these functions take those raw values (plus, where it
 * matters, the display unit) and produce human strings.
 */

export type DisplayUnit = 'm' | 'yd'

const YARD_METRES = 0.9144

/** Metres → the file's display unit (metres unchanged, or yards). */
export function toDisplayDistance(metres: number, unit: DisplayUnit): number {
  return unit === 'yd' ? metres / YARD_METRES : metres
}

/** A duration in ms as `m:ss` (or `h:mm:ss` past an hour). Rounds to whole seconds. */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`
  return `${m}:${ss}`
}

/**
 * A pace given as seconds per 100 (metres or yards) as `m:ss.d` — e.g. 106.44 → "1:46.4".
 * Returns "—" for a non-finite pace (zero distance).
 */
export function formatPace(secondsPer100: number): string {
  if (!Number.isFinite(secondsPer100) || secondsPer100 <= 0) return '—'
  const m = Math.floor(secondsPer100 / 60)
  const rest = secondsPer100 - m * 60
  const ss = rest.toFixed(1).padStart(4, '0') // "06.4"
  return `${m}:${ss}`
}

/** Group an integer with thousands separators: 1925 → "1,925". */
export function groupThousands(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** A distance in metres shown in the file's unit, e.g. "1,925 m" or "2,100 yd". */
export function formatDistance(metres: number, unit: DisplayUnit): string {
  return `${groupThousands(toDisplayDistance(metres, unit))} ${unit}`
}

/** The FIT epoch (seconds since 1989-12-31 00:00 UTC) offset from the Unix epoch. */
const FIT_EPOCH_OFFSET_S = 631065600

/** A FIT timestamp → "Fri 12 Jun 2026, 07:14" in the viewer's locale/zone. */
export function formatActivityDate(fitEpochSeconds: number): string {
  const d = new Date((fitEpochSeconds + FIT_EPOCH_OFFSET_S) * 1000)
  const date = d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${date}, ${time}`
}

/** Seconds per 100 display-units for a distance (metres) swum in a time (ms). */
export function paceSecondsPer100(metres: number, ms: number, unit: DisplayUnit): number {
  const hundreds = toDisplayDistance(metres, unit) / 100
  if (hundreds <= 0) return Infinity
  return ms / 1000 / hundreds
}
